import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SeasonRecapService } from '../../../core/providers/services/season-recap.service';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { environment } from '../../../../environments/environment';

import { UserSeasonPerformance } from '../../../shared/models/season-recap';

function buildShareMessage(perf: UserSeasonPerformance | null | undefined, seasonTitle?: string): string {
  const title = seasonTitle ?? 'la saison';
  if (perf?.participated) {
    return `J'ai terminé #${perf.rank} sur le thème "${title}" sur Wear It ! 🔥`;
  }
  return `Découvre le classement de la saison "${title}" sur Wear It ! 🏆`;
}

@Component({
  selector: 'ootd-season-recap',
  templateUrl: './season-recap.html',
  styleUrl: './season-recap.less',
  imports: [ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeasonRecapComponent {
  readonly recapService = inject(SeasonRecapService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  readonly isOpen = computed(() => this.recapService.isOpen());
  readonly recap = computed(() => this.recapService.recapResource.value());
  readonly isLoading = computed(() => this.recapService.recapResource.isLoading());
  readonly hasError = computed(() => Boolean(this.recapService.recapResource.error()));

  readonly season = computed(() => this.recap()?.season);
  readonly stats = computed(() => this.recap()?.stats);
  readonly podium = computed(() => this.recap()?.podium ?? []);
  readonly winner = computed(() => this.podium()[0] ?? null);
  readonly runnerUps = computed(() => this.podium().slice(1));
  readonly remainingTop = computed(() => (this.recap()?.top10 ?? []).slice(3));
  readonly userPerformance = computed(() => this.recap()?.userPerformance);

  close() {
    this.recapService.closeRecap();
  }

  discoverNewSeason() {
    this.close();
    this.router.navigate(['/discover']);
  }

  async shareResult() {
    const text = buildShareMessage(this.userPerformance(), this.season()?.title);
    const url = environment.appUrl;

    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({
          title: 'Wear It - Bilan de Saison',
          text,
          url,
          dialogTitle: 'Partager mon résultat',
        });
      } catch {}
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Wear It - Bilan de Saison',
          text,
          url,
        });
      } catch {}
      return;
    }

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        this.messageService.add({
          severity: 'success',
          summary: 'Lien copié',
          detail: 'Le résultat a été copié dans votre presse-papier !',
          life: 3000,
        });
      } catch {}
    }
  }
}
